import { useState } from 'react';

export default function Community() {
  const [formData, setFormData] = useState({ name: '', email: '', role: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));
      await fetch('https://readdy.ai/api/form/d7c49s2qofe8vli40trg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="community" className="py-20 bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">

        {/* Community Callout Box */}
        <div className="bg-gradient-to-br from-brand-500 to-accent-600 rounded-3xl p-8 lg:p-14 mb-16 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-5">
            <i className="ri-heart-fill text-white text-2xl"></i>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 font-heading">
            You Are Not Alone
          </h2>
          <p className="text-white/85 text-sm md:text-lg max-w-xl mx-auto font-body leading-relaxed mb-8">
            Families, patients, caregivers, and researchers — all coming together around CSCF. Whatever stage you are at, there is a place for you here.
          </p>
          <a
            href="https://www.facebook.com/share/g/1BT8NGBZVX/?mibextid=K35XfP"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-600 font-bold rounded-full hover:bg-brand-50 transition-colors whitespace-nowrap cursor-pointer font-body text-sm md:text-base"
          >
            <i className="ri-group-line"></i>
            Join the Community
          </a>
        </div>

        {/* Join Form — centered */}
        <div className="max-w-xl mx-auto">
          <div id="join-form" className="bg-white rounded-2xl border border-gray-100 p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-brand-50 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-brand-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 font-heading">Welcome!</h3>
                <p className="text-gray-500 font-body">Thank you for joining. We&apos;ll be in touch soon.</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Join the Community</h3>
                <p className="text-gray-500 text-sm mb-7 font-body">
                  Connect with families and access trusted CSCF resources.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Full name"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors font-body"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors font-body"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">I am a...</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors cursor-pointer font-body"
                    >
                      <option value="">Select your role</option>
                      <option value="parent">Parent / Caregiver</option>
                      <option value="patient">Person with CSCF</option>
                      <option value="medical">Medical Professional</option>
                      <option value="researcher">Researcher</option>
                      <option value="supporter">Supporter / Advocate</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                      Message <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about yourself or how we can help..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors resize-none font-body"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right font-body">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 bg-brand-500 text-white font-semibold rounded-full hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 font-body"
                  >
                    {submitting ? 'Joining...' : 'Join the Community'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
